import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import { GenericFormField, GenericSelect } from '@/styles/GenericForm';
import { GenericAttachmentsSection } from '@/styles/GenericAttachmentsSection';
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from '@/styles/GenericCommentsSection';
import { StandardButton, CloseButton, SaveChangesButton, CancelButton } from '@/styles/StandardButtons';
import { uploadProposalAttachments, deleteProposalAttachment } from '@/features/leads/proposals/store';
import { useUser } from '@/contexts/UserContext';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { colors } from '@/design-system/colors';
import { useEntityComments } from '@/hooks/useEntityComments';
import { useProject } from '@/components/ProjectSelector';
import { useProjectMembershipPrompt } from '@/hooks/useProjectMembershipPrompt';
import { ClientLeadCombobox, ClientLeadSelection } from '@/components/shared/ClientLeadCombobox';
import { ClientForm } from '@/features/people/clients/components/ClientForm';
import { createClientUser } from '@/features/people/clients/store';

interface FileAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string;
  created_at: string;
  created_by: string;
  created_by_user?: {
    display_name?: string | null;
  };
}

interface Lead {
  id: string;
  name: string;
  email: string;
}

interface Proposal {
  id: string;
  company_id: string;
  client_id: string | null;
  project_id?: string | null;
  template_id: string | null;
  name: string;
  content: string | null;
  status: string;
  total_amount: number;
  notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface ProposalsFormProps {
  mode: 'create' | 'edit';
  initialData?: Proposal;
  leads: Lead[];
  onSubmit: (data: any) => Promise<any> | void; // Can return the created/updated proposal
  onCancel: () => void;
  session: any;
  attachments?: FileAttachment[];
  onDeleteAttachment?: (id: string) => void;
  onSend?: (proposalId: string, proposalData?: any) => Promise<void> | void; // Updated to accept proposalId and optional proposal data
  error?: string | null;
}

const inputStyles = "font-form-input generic-form-field";

export function ProposalsForm({ 
  mode, 
  initialData, 
  leads, 
  onSubmit, 
  onCancel,
  attachments = [],
  onDeleteAttachment,
  onSend,
  error,
}: ProposalsFormProps) {
  const { companyId, user } = useUser();
  const { projects } = useProject();
  const [formData, setFormData] = useState<Record<string, any>>({
    name: initialData?.name || '',
    client_id: initialData?.client_id || '',
    project_id: initialData?.project_id || '',
    content: initialData?.content || '',
    status: initialData?.status || 'draft',
    total_amount: initialData?.total_amount || 0,
    notes: initialData?.notes || '',
  });

  const uploadProposalAttachmentsFn = useCallback(async (id: string, files: File[]): Promise<FileAttachment[]> => {
    if (!companyId || !user?.id) return [];
    return uploadProposalAttachments(id, files, companyId, user.id);
  }, [companyId, user]);
  const deleteProposalAttachmentFn = useCallback((id: string) => deleteProposalAttachment(id, user?.id), [user]);
  const {
    pendingFiles: selectedFiles,
    uploadedFiles,
    setUploadedFiles,
    handleUpload: handleFileUpload,
    handleRemovePending: handleRemoveSelectedFile,
    handleDelete: handleDeleteFileBase,
  } = useAttachmentStaging<FileAttachment>({
    entityId: initialData?.id,
    uploadFn: uploadProposalAttachmentsFn,
    deleteFn: deleteProposalAttachmentFn,
    initialUploadedFiles: attachments,
  });
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState<string | null>(null);
  const [selectedPreviewFileId, setSelectedPreviewFileId] = useState<string | null>(null);

  // The preview is the file's permanent KrewUp page inside the frame; the backend checks access when it loads
  const handleSelectPreview = useCallback((file: { id: string; file_url: string }) => {
    setSelectedPreviewFileId(file.id);
    setSelectedPreviewUrl(file.file_url);
  }, []);

  const handleDeleteFile = useCallback(async (attachmentId: string) => {
    const ok = await handleDeleteFileBase(attachmentId);
    if (ok) {
      if (selectedPreviewFileId === attachmentId) {
        setSelectedPreviewFileId(null);
        setSelectedPreviewUrl(null);
      }
      onDeleteAttachment?.(attachmentId);
    }
  }, [selectedPreviewFileId, handleDeleteFileBase, onDeleteAttachment]);
  const formRef = React.useRef<HTMLFormElement>(null);
  const initialValuesRef = React.useRef<Record<string, any>>({});
  // `undefined` here means "the init effect hasn't run yet" -- distinct
  // from `null`, which is the legitimate currentProposalId for a create
  // (no initialData.id) proposal. Using `null` as the initial value here
  // made `currentProposalId !== initializedProposalIdRef.current` evaluate
  // to `null !== null` (false) on a brand-new proposal's first render, so
  // the init effect below never ran and initialValuesRef.current stayed
  // `{}` -- comparing formData against `{}` looked dirty immediately,
  // showing "unsaved progress" on an untouched New Proposal.
  const initializedProposalIdRef = React.useRef<string | null | undefined>(undefined);

  // Comments (generic architecture, entity_type='proposal'): load / add / delete through the API, with their authors (edit mode only).
  const { comments: localComments, addComment: handleAddComment, deleteComment: handleDeleteComment } =
    useEntityComments('proposal', mode === 'edit' ? initialData?.id : undefined);

  // Lead selector (generic Client/Lead combobox, shared with New Project's
  // Client field -- see src/components/shared/ClientLeadCombobox.tsx).
  const [leadDisplayName, setLeadDisplayName] = useState('');
  const [isAddLeadOpen, setIsAddLeadOpen] = useState(false);
  const [leadListRefreshToken, setLeadListRefreshToken] = useState(0);

  const handleLeadChange = useCallback((selection: ClientLeadSelection) => {
    setLeadDisplayName(selection.client_name);
    handleChange('client_id', selection.client_id || '');
  }, []);

  const handleSaveNewLead = async (leadFormData: any) => {
    if (!companyId || !user?.id) return;
    const created = await createClientUser(companyId, user.id, leadFormData);
    handleLeadChange({ client_id: created.id, client_name: created.name });
    // Force ClientLeadCombobox to refetch so the new lead shows up in its
    // list immediately, without closing/remounting the Proposal modal.
    setLeadListRefreshToken((prev) => prev + 1);
    setIsAddLeadOpen(false);
  };

  // Define form fields for GenericForm
  const proposalFields = useMemo<GenericFormField[]>(() => [
    { name: 'name', label: 'Proposal Name', type: 'text', placeholder: 'Enter proposal name', row: 1, colSpan: 2 },
    {
      // phase192: a draft can be saved without a client (null); SENDING one needs it (checked below and by the server).
      // Rendered via ClientLeadCombobox (renderField's client_id special case below), not GenericSelect -- `type`/`options`
      // here only drive the label/row layout metadata.
      name: 'client_id',
      label: 'Lead',
      type: 'select',
      row: 2,
    },
    { 
      name: 'status', 
      label: 'Status', 
      type: 'select', 
      row: 2,
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Sent', value: 'sent' },
        { label: 'Accepted', value: 'accepted' },
        { label: 'Declined', value: 'declined' },
        { label: 'Feedback', value: 'feedback' },
        { label: 'Revision', value: 'revision' },
      ]
    },
    { name: 'total_amount', label: 'Total Amount', type: 'number', placeholder: '0.00', row: 3 },
    // optional: a proposal is often written to win a job that is not a project yet
    { name: 'project_id', label: 'Project', type: 'select', row: 3, searchable: true, options: projects.map((project) => ({ label: project.name, value: project.id })) },
    { name: 'content', label: 'Content', type: 'textarea', placeholder: 'Enter proposal content', row: 4, colSpan: 2 },
  ], [projects]);

  // Initialize form data and capture initial values when form opens
  useEffect(() => {
    const currentProposalId = initialData?.id || null;
    
    // Only initialize if this is a new proposal (no ID) or a different proposal
    if (currentProposalId !== initializedProposalIdRef.current) {
      const initialValues = {
        name: initialData?.name || '',
        client_id: initialData?.client_id || '',
        project_id: initialData?.project_id || '',
        content: initialData?.content || '',
        status: initialData?.status || 'draft',
        total_amount: initialData?.total_amount || 0,
        notes: initialData?.notes || '',
      };
      
      const deepCopy = JSON.parse(JSON.stringify(initialValues));
      initialValuesRef.current = deepCopy;
      setFormData(deepCopy);
      initializedProposalIdRef.current = currentProposalId;

      const matchedLead = leads.find((l) => l.id === initialValues.client_id);
      setLeadDisplayName(matchedLead ? matchedLead.name : '');
    }
  }, [initialData?.id]); // Only depend on the ID, not the whole object

  // Helper function to check if form data has changed
  const hasFormChanged = (current: Record<string, any>, initial: Record<string, any>): boolean => {
    const currentKeys = Object.keys(current);
    const initialKeys = Object.keys(initial);
    const allKeysSet = new Set<string>();
    currentKeys.forEach(key => allKeysSet.add(key));
    initialKeys.forEach(key => allKeysSet.add(key));
    const allKeys = Array.from(allKeysSet);
    
    for (const key of allKeys) {
      const currentValue = current[key];
      const initialValue = initial[key];
      
      const currentStr = currentValue == null ? '' : String(currentValue).trim();
      const initialStr = initialValue == null ? '' : String(initialValue).trim();
      
      if (currentStr !== initialStr) {
        return true;
      }
    }
    return false;
  };

  const { ensureMembers, prompt: membershipPrompt } = useProjectMembershipPrompt();
  const [sendProblem, setSendProblem] = useState<string | null>(null);

  const handleChange = (name: string, value: any) => {
    if (name === 'client_id') setSendProblem(null);
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent, skipPreventDefault = false) => {
    if (!skipPreventDefault) {
      e.preventDefault();
    }
    console.log('📝 [ProposalsForm] Form submitted', { mode, formData });
    
    try {
      // a proposal that belongs to a project: its client must be a member of it (asked once, now that it is being saved)
      if (!(await ensureMembers(formData.project_id || null, [{ id: formData.client_id || '', name: leadDisplayName || 'The client' }]))) return undefined;
      const result = await onSubmit({
        ...formData,
        name: formData.name.trim(),
        client_id: formData.client_id || null,
        project_id: formData.project_id || null,
        content: formData.content.trim(),
        status: formData.status,
        total_amount: parseFloat(formData.total_amount.toString()) || 0,
        notes: formData.notes.trim(),
        attachmentsFiles: selectedFiles
      });
      console.log('✅ [ProposalsForm] onSubmit completed', result);
      return result;
    } catch (error) {
      console.error('❌ [ProposalsForm] Error in onSubmit', error);
      throw error;
    }
  };

  useEffect(() => {
    if (initialData?.id) {
      setUploadedFiles(attachments || []);
      if (attachments && attachments.length > 0) {
        const sortedAttachments = [...attachments].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        const newest = sortedAttachments[0];
        // only the first time a given file arrives (the list is re-read after saves)
        if (selectedPreviewFileId !== newest.id) {
          handleSelectPreview(newest);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.id, attachments, setUploadedFiles]);

  const handleCloseAttempt = () => {
    // Check if form has actually changed by comparing current data to initial values
    const isActuallyDirty = hasFormChanged(formData, initialValuesRef.current);
    
    if (isActuallyDirty) {
      if (window.confirm("Are you sure you want to lose all unsaved progress?")) {
        // Reset the initialized proposal ID ref when closing
        initializedProposalIdRef.current = null;
        onCancel();
      }
      // If user cancels the confirmation, don't close the form
    } else {
      // Reset the initialized proposal ID ref when closing
      initializedProposalIdRef.current = null;
      // No changes, close immediately
      onCancel();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    // Only the backdrop itself (e.target === e.currentTarget), never a
    // descendant -- matches GenericForm.tsx's own overlay-click pattern.
    // The previous `formRef.current.contains(e.target)` check broke for
    // ANY child modal portaled elsewhere in the DOM (e.g. the "Add new
    // Lead" ClientForm, portaled to document.body): React bubbles
    // synthetic events up the COMPONENT tree even across portals, but
    // `e.target` is the real DOM node clicked, which is never inside
    // `formRef` for a portaled child -- so every click inside that child
    // modal (e.g. its own Cancel button) was misread as "clicked outside
    // the Proposal form" and closed the Proposal underneath it too.
    if (e.target === e.currentTarget) {
      handleCloseAttempt();
    }
  };

  // Render field based on type
  const renderField = (field: GenericFormField) => {
    if (field.name === 'client_id') {
      return (
        <ClientLeadCombobox
          value={leadDisplayName}
          onChange={handleLeadChange}
          placeholder="Select"
          onCreateNew={() => setIsAddLeadOpen(true)}
          refreshToken={leadListRefreshToken}
        />
      );
    } else if (field.type === 'select') {
      return (
        <GenericSelect
          value={formData[field.name] || ''}
          onValueChange={(value) => handleChange(field.name, value)}
          disabled={field.disabled}
          required={field.required}
          placeholder={field.placeholder}
          searchable={field.name === 'project_id'}
          options={field.options || []}
        />
      );
    } else if (field.type === 'textarea') {
      return (
        <Textarea
          id={field.name}
          value={formData[field.name] || ''}
          onChange={(e) => handleChange(field.name, e.target.value)}
          placeholder={field.placeholder}
          disabled={field.disabled}
        />
      );
    } else if (field.type === 'number' && field.name === 'total_amount') {
      return (
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none text-sm">$</span>
          <Input
            id={field.name}
            type="number"
            step="0.01"
            value={formData[field.name] || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(field.name, parseFloat(e.target.value) || 0)}
            placeholder={field.placeholder}
            className={`${inputStyles} pl-10 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
            disabled={field.disabled}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              // Prevent arrow keys from changing number values
              if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onWheel={(e: React.WheelEvent<HTMLInputElement>) => {
              // Prevent trackpad/mouse wheel from changing number values
              e.preventDefault();
              e.stopPropagation();
            }}
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
              // Prevent wheel events on focused number inputs
              e.currentTarget.addEventListener('wheel', (event: WheelEvent) => {
                event.preventDefault();
              }, { passive: false });
            }}
            style={{
              MozAppearance: 'textfield',
              WebkitAppearance: 'none',
              textIndent: '0.7rem',
            }}
          />
        </div>
      );
    } else {
      return (
        <Input
          id={field.name}
          type={field.type}
          value={formData[field.name] || ''}
          onChange={(e) => handleChange(field.name, e.target.value)}
          placeholder={field.placeholder}
          className={field.type === 'number' 
            ? `${inputStyles} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`
            : inputStyles
          }
          disabled={field.disabled}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            // Prevent arrow keys from changing number values
            if (field.type === 'number' && ['ArrowUp', 'ArrowDown'].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onWheel={(e: React.WheelEvent<HTMLInputElement>) => {
            // Prevent trackpad/mouse wheel from changing number values
            if (field.type === 'number') {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
            // Prevent wheel events on focused number inputs
            if (field.type === 'number') {
              e.currentTarget.addEventListener('wheel', (event: WheelEvent) => {
                event.preventDefault();
              }, { passive: false });
            }
          }}
        />
      );
    }
  };

  // Group fields by row
  const fieldsByRow = proposalFields.reduce((acc, field) => {
    const row = field.row || 999;
    if (!acc[row]) acc[row] = [];
    acc[row].push(field);
    return acc;
  }, {} as Record<number, GenericFormField[]>);

  const sortedRows = Object.keys(fieldsByRow).sort((a, b) => Number(a) - Number(b));

  const formContent = (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-[1px]"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={handleOverlayClick}
    >
      {membershipPrompt}
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-2xl flex flex-col m-4"
        style={{ width: '80vw', maxHeight: '80vh' }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between p-6 border-b border-gray-200 rounded-t-xl"
          style={{ backgroundColor: colors.formHeaderFooter }}
        >
          <h2 className="font-form-title">
            {mode === 'create' ? 'Create Proposal' : 'Edit Proposal'}
          </h2>
          <CloseButton onClick={handleCloseAttempt} />
        </div>

        {/* Body - Two Column Layout */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Side - Form */}
          <div className="flex-1 min-w-0 overflow-y-auto p-8 border-r border-gray-200">
            {(error || sendProblem) && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
                {error || sendProblem}
              </div>
            )}
            
            <div className="space-y-6">
              {sortedRows.map((rowKey) => (
                <div
                  key={rowKey}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  {fieldsByRow[Number(rowKey)].map((field) => (
                    <div
                      key={field.name}
                      className={cn(
                        "space-y-2",
                        field.colSpan === 2 ? "md:col-span-2" : "md:col-span-1"
                      )}
                    >
                      <Label htmlFor={field.name} className="font-form-label">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </Label>
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Version History Section */}
            {uploadedFiles.length > 0 && (
              <div className="mt-6 space-y-2">
                <Label className="font-form-label">Version History</Label>
                <div className="border-2 border-border rounded-md overflow-hidden min-w-0">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-gray-600">File</th>
                        <th className="text-left px-3 py-2 font-medium text-gray-600">Version</th>
                        <th className="text-left px-3 py-2 font-medium text-gray-600">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {uploadedFiles
                        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                        .map((file, index) => (
                        <tr 
                          key={file.id}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => handleSelectPreview(file)}
                        >
                          <td className="px-3 py-2 text-gray-700 truncate max-w-[12.5rem]" title={file.file_name}>
                            {file.file_name}
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            v{uploadedFiles.length - index}
                          </td>
                          <td className="px-3 py-2 text-gray-500 text-xs">
                            {format(new Date(file.created_at), 'dd/MM/yyyy')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Attachments Section */}
            <div className="mt-6 min-w-0">
              <GenericAttachmentsSection
                attachments={uploadedFiles.map(file => ({
                  id: file.id,
                  file_name: file.file_name,
                  file_type: file.file_type,
                  file_size: file.file_size,
                  file_url: file.file_url,
                  created_at: file.created_at,
                  created_by: file.created_by,
                  created_by_user: file.created_by_user
                }))}
                pendingFiles={selectedFiles}
                onUpload={handleFileUpload}
                onDelete={handleDeleteFile}
                onRemovePending={handleRemoveSelectedFile}
                mode={mode}
                // a proposal belongs to a lead, not to a project: there is no project Drive to browse
                projectId={null}
              />
            </div>

            {/* Comments Section (generic architecture, entity_type='proposal') --
                same column/width as Version History, Content and Attachments above;
                never spans under the PDF Preview panel on the right. */}
            <div className="mt-6">
              <GenericCommentsSection
                comments={localComments}
                onAddComment={handleAddComment}
                onDeleteComment={handleDeleteComment}
                mode={mode}
                audiences={TEAM_CLIENT_VENDOR_AUDIENCES}
                placeholder={mode === 'create' ? 'Comments are available after creating the proposal' : 'Add a comment...'}
              />
            </div>
          </div>

          {/* Right Side - PDF Preview */}
          <div className="flex-1 overflow-y-auto p-8 bg-gray-50">
            <h3 className="text-lg font-semibold mb-4">PDF Preview</h3>
            {selectedPreviewUrl ? (
              <div className="border rounded-lg bg-white min-h-[37.5rem] overflow-hidden">
                <iframe
                  src={selectedPreviewUrl}
                  className="w-full h-[37.5rem]"
                  title="PDF Preview"
                  allow="fullscreen"
                />
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 min-h-[37.5rem] flex items-center justify-center">
                <div className="text-center text-gray-500 p-8">
                  <svg 
                    className="mx-auto h-12 w-12 text-gray-400 mb-4" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                    />
                  </svg>
                  <p className="text-sm font-medium mb-2">PDF Preview</p>
                  <p className="text-xs">Click on a version to preview</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer with Send, Save, Cancel buttons */}
        <div 
          className="p-6 border-t border-gray-200 flex justify-end gap-4 rounded-b-xl"
          style={{ backgroundColor: colors.formHeaderFooter }}
        >
          <CancelButton
            onClick={handleCloseAttempt}
          >
            Cancel
          </CancelButton>
          {onSend && (
            <StandardButton
              customVariant="white"
              onClick={async () => {
                try {
                  console.log('📧 [ProposalsForm] Send button clicked', { mode, hasInitialData: !!initialData?.id });
                  // saving without a client is fine; sending without one is not
                  if (!formData.client_id) { setSendProblem('Select a client before sending the proposal.'); return; }
                  setSendProblem(null);
                  
                  // First save the proposal (create or update)
                  const savedResult = await handleSubmit(new Event('submit') as any, true);
                  
                  // Get the proposal ID - either from saved result or existing initialData
                  let proposalId: string | null = null;
                  
                  if (mode === 'edit' && initialData?.id) {
                    // Edit mode - use existing ID
                    proposalId = initialData.id;
                  } else if (savedResult?.id) {
                    // Create mode - get ID from saved result
                    proposalId = savedResult.id;
                  } else if (savedResult?.data?.id) {
                    // Alternative response format
                    proposalId = savedResult.data.id;
                  }
                  
                  console.log('📧 [ProposalsForm] Proposal ID for send', { proposalId, savedResult });
                  
                  if (!proposalId) {
                    console.error('❌ [ProposalsForm] No proposal ID available after save');
                    alert('Failed to save proposal. Please try saving first, then sending.');
                    return;
                  }
                  
                  // Now send the proposal - pass the saved proposal data directly
                  await onSend(proposalId, savedResult);
                } catch (error: any) {
                  console.error('❌ [ProposalsForm] Error in send handler:', error);
                  alert(error.message || 'Failed to save and send proposal');
                }
              }}
              type="button"
            >
              Send to Client
            </StandardButton>
          )}
          <SaveChangesButton>
            Save Changes
          </SaveChangesButton>
        </div>
      </form>
      {/* Stacked on top of the proposal form -- does not close it -- so
          picking "Add new" from the Lead selector mid-flow doesn't lose
          proposal edits. Outside <form> to avoid a nested-form. */}
      <ClientForm
        isOpen={isAddLeadOpen}
        client={null}
        onClose={() => setIsAddLeadOpen(false)}
        onSave={handleSaveNewLead}
      />
    </div>
  );

  return createPortal(formContent, document.body);
}

